import tinycolor from 'tinycolor2'

import {
  THEME_CONTENT_CORRECTED_PROPERTIES,
  THEME_CONTENT_CORRECTION_KEY_SEPARATOR,
  THEME_CONTENT_IMPORTANT_PRIORITY,
  THEME_CONTENT_ORIGINAL_STYLE_ATTRIBUTE,
} from '../../constants/themeMode'

import {
  getThemeContentContainer,
  hasThemeContentBackgroundImage,
} from './themeContent'

function getStyleElement(element, style) {
  const styleElement = element.ownerDocument.createElement('span')

  styleElement.setAttribute('style', style)

  return styleElement
}

function getStyleSignature(style) {
  return Array.from(style)
    .map(
      property =>
        `${property}:${style.getPropertyValue(property)}:${style.getPropertyPriority(property)}`
    )
    .sort()
    .join(';')
}

function isUneditedCorrection(element, property) {
  return (
    element.style.getPropertyPriority(property) ===
    THEME_CONTENT_IMPORTANT_PRIORITY
  )
}

function isKeptColor(element, property) {
  const color = tinycolor(element.style.getPropertyValue(property))

  return (
    color.isValid() &&
    color.getAlpha() > 0 &&
    (!element.hasAttribute(THEME_CONTENT_ORIGINAL_STYLE_ATTRIBUTE) ||
      !isUneditedCorrection(element, property))
  )
}

function restoreCorrectedStyle(element) {
  const originalStyle = element.getAttribute(
    THEME_CONTENT_ORIGINAL_STYLE_ATTRIBUTE
  )
  const original = getStyleElement(element, originalStyle).style

  THEME_CONTENT_CORRECTED_PROPERTIES.filter(property =>
    isUneditedCorrection(element, property)
  ).forEach(property => {
    const value = original.getPropertyValue(property)

    if (value) {
      element.style.setProperty(
        property,
        value,
        original.getPropertyPriority(property)
      )
    } else {
      element.style.removeProperty(property)
    }
  })

  if (getStyleSignature(element.style) === getStyleSignature(original)) {
    element.setAttribute('style', originalStyle)
  }

  if (!element.getAttribute('style')) {
    element.removeAttribute('style')
  }

  element.removeAttribute(THEME_CONTENT_ORIGINAL_STYLE_ATTRIBUTE)
}

// Moved text keeps a corrected color inline without the marker: mapped back by value
function restoreMovedCorrectedColors(element, corrections) {
  THEME_CONTENT_CORRECTED_PROPERTIES.forEach(property => {
    const value = element.style.getPropertyValue(property)
    const key = value ? getThemeContentCorrectionKey(property, value) : null

    if (corrections.has(key)) {
      const original = corrections.get(key)

      if (original) {
        element.style.setProperty(property, original)
      } else {
        element.style.removeProperty(property)
      }
    }
  })

  if (!element.getAttribute('style')) {
    element.removeAttribute('style')
  }
}

function hasThemeContentCorrections(html, corrections) {
  return (
    html.includes(THEME_CONTENT_ORIGINAL_STYLE_ATTRIBUTE) ||
    Array.from(corrections.keys()).some(key =>
      html.includes(key.split(THEME_CONTENT_CORRECTION_KEY_SEPARATOR)[1])
    )
  )
}

export function getThemeContentCorrectionKey(property, value) {
  return [property, tinycolor(value).toHexString()].join(
    THEME_CONTENT_CORRECTION_KEY_SEPARATOR
  )
}

export function getThemeContentCorrections(html) {
  const corrections = new Map()

  if (html?.includes(THEME_CONTENT_ORIGINAL_STYLE_ATTRIBUTE)) {
    Array.from(
      getThemeContentContainer(html).querySelectorAll(
        `[${THEME_CONTENT_ORIGINAL_STYLE_ATTRIBUTE}]`
      )
    )
      .filter(element => !hasThemeContentBackgroundImage(element))
      .forEach(element => {
        const original = getStyleElement(
          element,
          element.getAttribute(THEME_CONTENT_ORIGINAL_STYLE_ATTRIBUTE)
        ).style

        THEME_CONTENT_CORRECTED_PROPERTIES.forEach(property => {
          const value = element.style.getPropertyValue(property)

          if (value && isUneditedCorrection(element, property)) {
            corrections.set(
              getThemeContentCorrectionKey(property, value),
              original.getPropertyValue(property)
            )
          }
        })
      })
  }

  return corrections
}

export function getThemeContentKeptColorKeys(html) {
  return new Set(
    Array.from(
      getThemeContentContainer(html).querySelectorAll('[style]')
    ).flatMap(element =>
      THEME_CONTENT_CORRECTED_PROPERTIES.filter(property =>
        isKeptColor(element, property)
      ).map(property =>
        getThemeContentCorrectionKey(
          property,
          element.style.getPropertyValue(property)
        )
      )
    )
  )
}

export function restoreThemeContentHtml(html, corrections = new Map()) {
  if (!html || !hasThemeContentCorrections(html, corrections)) {
    return html
  }

  const container = getThemeContentContainer(html)

  container
    .querySelectorAll(
      `[style]:not([${THEME_CONTENT_ORIGINAL_STYLE_ATTRIBUTE}])`
    )
    .forEach(element => restoreMovedCorrectedColors(element, corrections))

  container
    .querySelectorAll(`[${THEME_CONTENT_ORIGINAL_STYLE_ATTRIBUTE}]`)
    .forEach(restoreCorrectedStyle)

  return container.innerHTML
}
